import { sendClientLog } from "@/lib/clientLogger";
import { isEditableElement } from "@/lib/editableElement";
import { currentRouteTemplate } from "@/lib/routeTemplate";
import { APP_VIEWPORT_OFFSET_TOP_CSS_VAR } from "@/lib/viewportRuntime";

/**
 * Detects the class of defect where **the server is healthy, the document is correct, and the product
 * is unusable** — and which therefore reaches nobody's dashboard.
 *
 * The four mobile-chat incidents of August 2026 were each detected by the owner's phone. None produced
 * a 5xx, an exception, a failed fetch, a console error or a layout shift; the DOM was completely
 * correct every time. What was wrong was a RELATIONSHIP — the visible rectangle versus where a
 * `position: fixed` shell was painted — and no single element carries that.
 *
 * Two invariants ship here. Both are event-driven and debounced; **neither polls and neither scans the
 * DOM.** That is not a style preference: `layoutHealthMonitor.ts` is off in production precisely
 * because it polls and forces synchronous reflow, an INP tax on exactly the mobile devices this is
 * meant to protect — and its violation kinds are horizontal-overflow only, so it could not have seen
 * any of this even switched on. Enabling it was the one option the design rejected outright.
 *
 * Design: `designs/01-now/2026-08-16-unusable-page-observability-design.md` (Layer 2 and I2).
 * Incident: `designs/01-now/2026-08-11-mobile-chat-viewport-ownership-and-composer-design.md` §3e.
 */

export type UnusablePageInvariant = "focus-zoom" | "offset-contract";

/**
 * ⚠️ These three strings are the ONLY link between this file and CloudWatch. Nothing checks them at
 * compile time — the same standing caveat as `SSR_DEGRADED` and `EMAIL_SIGNIN_TOKEN_MINT_FAILED`.
 * `monitoring-stack.ts` filters on them and `main-stack.test.ts` pins the pattern; changing one here
 * without changing both there produces a signal that emits perfectly and is counted by nothing.
 *
 * They are also deliberately digit-free and under 24 characters per underscore-joined run, so
 * `AjaxClientLogController.redact` cannot mistake a marker for a secret token and blank it — which is
 * exactly what it did to the chunk hashes during the 2026-08-15 triage.
 */
export const UNUSABLE_PAGE_VIOLATION_MARKER = "UNUSABLE_PAGE_VIOLATION";
export const UNUSABLE_PAGE_HEARTBEAT_MARKER = "UNUSABLE_PAGE_PROBE_RAN";
export const UNUSABLE_PAGE_DETAIL_MARKER = "UNUSABLE_PAGE_DETAIL";

/**
 * The epsilon that separates "at rest" from "zoomed".
 *
 * Engines report fractional scales at rest, so an exact `=== 1` test would be noise. 1.02 sits far
 * below the smallest zoom the engine actually applies: iOS Safari's focus zoom is exactly
 * `16 / font-size`, so the gentlest case in the product — a 15px field — is 1.067, and `text-sm` is
 * 1.142. Measured on an iPhone 16 Pro / iOS 18.1, design §3e.
 */
const SCALE_AT_REST_TOLERANCE = 1.02;

/**
 * How far the published offset may sit from the engine's before the runtime is lying to the layout.
 *
 * Both sides are rounded to whole pixels already (`readViewportOffsetTopPx` rounds; the CSS var is
 * written as an integer `px`), so this only absorbs a one-frame skew between the runtime's handler and
 * this one. The failure it exists to catch was 294px.
 */
const OFFSET_AGREEMENT_TOLERANCE_PX = 2;

/**
 * Debounce for the viewport handlers. Long enough that `viewportRuntime` — which listens to the SAME
 * `visualViewport` events — has certainly published before we read what it published. Reading a value
 * one event stale would make I2 a false-positive generator on every keyboard open.
 */
const VIEWPORT_SETTLE_MS = 250;

/**
 * How long a focus stays judgeable, measured from the `focusin` that armed it.
 *
 * **This bound is the fix for a false-positive class measured in prod on 2026-08-26: 16 of 23
 * violations in seven days were desktop engines, which have no focus-zoom behaviour at all.** The
 * mechanism was that `scaleBeforeFocus` is a latch armed at `focusin` and released at `focusout`,
 * while `evaluate()` re-runs on *every* later `visualViewport` resize. A member who focused the
 * editor and then pinch-zoomed to read more comfortably — a minute later, ten minutes later, still
 * focused — was judged against the scale from the moment they first tapped in, and their own
 * deliberate zoom was reported as the engine's.
 *
 * The class comment said "we only judge a focus we watched begin from rest", and that was true; what
 * it missed is that it went on judging that same focus for as long as the field held it. An engine
 * zoom is a direct consequence of the focus and lands within a few hundred milliseconds
 * ({@link FOCUS_SETTLE_MS}); two seconds is generous for a slow device and still far below the
 * timescale on which a human decides to zoom.
 */
const FOCUS_ZOOM_JUDGEMENT_WINDOW_MS = 2_000;

/**
 * The computed font size at or above which an engine has no reason to zoom a focused field.
 *
 * iOS Safari's rule is exactly `font-size < 16px`, and the zoom it applies is `16 / font-size`. So a
 * field measuring 16px or more cannot have provoked an engine focus zoom, and a scale change observed
 * around one is somebody's own pinch. Every one of the 2026-08-26 false positives reported
 * `fontSizePx=16`.
 *
 * This is read from the invariant's own definition rather than from a platform sniff on purpose: a
 * user-agent test would also have suppressed a real Android or future-engine regression, whereas "the
 * field was too big for the engine to zoom" stays true wherever it is evaluated.
 */
const ENGINE_ZOOM_FONT_SIZE_CEILING_PX = 16;

/**
 * How long after a focus to judge the zoom. The engine zoom follows the focus rather than accompanying
 * it, so an immediate read would always find `scale === 1` and the signal would never fire.
 */
const FOCUS_SETTLE_MS = 450;

/** Give the page a moment of real use before claiming the probe is alive. */
const HEARTBEAT_DELAY_MS = 5_000;

/**
 * Sampling, per principle 1 of the design and per the owner's decision.
 *
 * The DETAIL rate is the owner decision (1-in-100, capped at one per session): violations of this class
 * are not rare-and-precious, they are every session on the affected engine, so the hundredth payload
 * says exactly what the first one did — and the iOS rig reproduces any of them locally in about two
 * minutes, so the payload only has to name the field and the engine.
 *
 * The HEARTBEAT rate is a judgement, not a decision handed down: its only job is to keep "no
 * violations" distinguishable from "the probe was refactored out", and at 1-in-10 sessions that
 * question is answerable daily at a tenth of the log cost of a per-session line. It is a floor on
 * cost, not on fidelity — see §9.5 of the design for what it consequently cannot tell you.
 */
const DETAIL_SAMPLE_RATE = 0.01;
const HEARTBEAT_SAMPLE_RATE = 0.1;

/** Bounds the `route` dimension against a path a visitor can type. */
const MAX_ROUTE_LENGTH = 120;

export interface UnusablePageReport {
    kind: "violation" | "heartbeat" | "detail";
    invariant: UnusablePageInvariant | null;
    route: string;
    engine: string;
    message: string;
    extra: Record<string, unknown>;
}

export interface UnusablePageSignalOptions {
    viewportSettleMs: number;
    focusSettleMs: number;
    heartbeatDelayMs: number;
    detailSampleRate: number;
    heartbeatSampleRate: number;
    /** Injected so specs are deterministic; production uses `Math.random`. */
    random: () => number;
    reporter: (report: UnusablePageReport) => void;
}

const DEFAULT_OPTIONS: UnusablePageSignalOptions = {
    viewportSettleMs: VIEWPORT_SETTLE_MS,
    focusSettleMs: FOCUS_SETTLE_MS,
    heartbeatDelayMs: HEARTBEAT_DELAY_MS,
    detailSampleRate: DETAIL_SAMPLE_RATE,
    heartbeatSampleRate: HEARTBEAT_SAMPLE_RATE,
    random: Math.random,
    reporter: reportToBackend,
};

// ---------------------------------------------------------------------------------------------
// The two invariants, as pure functions. Kept pure so a spec can state a measured reading from the
// device rig verbatim and assert the verdict, without a viewport, a keyboard or a timer.
// ---------------------------------------------------------------------------------------------

export type InvariantVerdict = "not-applicable" | "pass" | "violation";

/**
 * **I3 / Layer 2 — a field we rendered provoked an engine zoom.**
 *
 * iOS Safari auto-zooms any focused form control whose computed font-size is under 16px. It is the
 * engine's readability behaviour, not something the page asked for, and it persists after blur — the
 * page stays zoomed until a reload. On `/messages` it also became the trigger for a page thrown off
 * the screen, but the cost everywhere else is a zoom the reader did not ask for and cannot easily undo.
 *
 * A member who pinch-zooms and THEN taps a field is ruled out by `scaleBeforeFocus`: we only judge a
 * focus we watched begin from rest. A focus that predates the probe (a bfcache restore into a focused
 * field) is `not-applicable` rather than guessed at, which makes the resulting count a floor.
 *
 * **Two further exclusions were added on 2026-08-26, after prod showed 16 of 23 violations in a week
 * coming from desktop engines that have no focus-zoom behaviour at all** — see
 * {@link FOCUS_ZOOM_JUDGEMENT_WINDOW_MS} for the latch bug that produced them and
 * {@link ENGINE_ZOOM_FONT_SIZE_CEILING_PX} for why the field's own size settles it. Both are stated as
 * properties of the engine behaviour being detected, not as platform tests, so a real regression on an
 * engine nobody has thought about still fires.
 */
export function evaluateFocusZoom(reading: {
    editableFocused: boolean;
    scaleBeforeFocus: number | null;
    scaleNow: number;
    /** Milliseconds since the `focusin` that armed `scaleBeforeFocus`; `null` when unknown. */
    msSinceFocus: number | null;
    /** Computed font size of the focused field in px; `null` when it could not be read. */
    fontSizePx: number | null;
}): InvariantVerdict {
    if (!reading.editableFocused || reading.scaleBeforeFocus === null) {
        return "not-applicable";
    }
    if (reading.scaleBeforeFocus > SCALE_AT_REST_TOLERANCE) {
        // The member had already zoomed the page themselves. Their zoom, their business.
        return "not-applicable";
    }
    if (reading.msSinceFocus === null || reading.msSinceFocus > FOCUS_ZOOM_JUDGEMENT_WINDOW_MS) {
        // The focus is still held, but far too long ago for a zoom now to be the engine's answer to it.
        return "not-applicable";
    }
    if (reading.fontSizePx !== null && reading.fontSizePx >= ENGINE_ZOOM_FONT_SIZE_CEILING_PX) {
        // Big enough that no engine would have zoomed it, so the scale change is somebody's own pinch.
        return "not-applicable";
    }
    return reading.scaleNow > SCALE_AT_REST_TOLERANCE ? "violation" : "pass";
}

/**
 * **I2 — the published offset versus the engine's.** The highest-fidelity signal in the design,
 * because it compares two numbers that must agree by construction: a violation is never a matter of
 * taste, never weather, and never a threshold anyone can argue about.
 *
 * On 2026-08-16, on prod, `--seavien-app-viewport-offset-top` published `0px` while
 * `visualViewport.offsetTop` was a genuine 294px keyboard reveal. `messages.jte`'s `<main>` is
 * `position: fixed; top: var(--seavien-app-viewport-offset-top)`, so the shell was pinned 294px ABOVE
 * the visible rectangle and the member saw a blank screen with the composer clipped against the top
 * edge.
 *
 * ⚠️ **The exclusion below is stated independently of `viewportRuntime`'s own predicate, and that is
 * load-bearing rather than sloppy.** There is exactly one state where the two numbers are ALLOWED to
 * disagree: a reader panning a zoomed page, which the runtime refuses to follow so a zoomed reader can
 * still pan down the page (WCAG 1.4.4/1.4.10). `viewportRuntime` has a predicate for that state, and
 * importing it here would have been the natural-looking mistake — reverting the 2026-08-16 focus
 * invariant CHANGES that predicate, so a detector sharing it goes blind in exactly the moment it is
 * needed. What is written here instead is a claim about the product ("a keyboard reveal must be
 * tracked"), it is strictly weaker than the runtime's rule, and it carries no copy of the runtime's
 * threshold constant to drift against.
 */
export function evaluateOffsetContract(reading: {
    hasContract: boolean;
    editableFocused: boolean;
    scale: number;
    publishedOffsetTopPx: number;
    engineOffsetTopPx: number;
}): InvariantVerdict {
    if (!reading.hasContract) {
        return "not-applicable";
    }
    if (!reading.editableFocused && reading.scale > 1) {
        // A pinch-zoom pan. Nobody's keyboard is open, so a disagreement here is the runtime doing its
        // job rather than failing at it.
        return "not-applicable";
    }
    const disagreementPx = Math.abs(reading.publishedOffsetTopPx - reading.engineOffsetTopPx);
    return disagreementPx > OFFSET_AGREEMENT_TOLERANCE_PX ? "violation" : "pass";
}

// ---------------------------------------------------------------------------------------------
// Dimensions
// ---------------------------------------------------------------------------------------------

/**
 * A coarse, CLOSED vocabulary for the engine dimension.
 *
 * Segmenting by engine is the difference between a signal and a rounding error: the 08-16 defect
 * existed on 100% of iOS Safari sessions and 0% of everything else, so in an aggregate it is invisible.
 * iOS Firefox is called out separately for the same reason — it was "mostly fine" on the same phone.
 *
 * Closed by construction: the value is assembled from two fixed ladders, so an exotic user agent
 * lands on `desktop-other` rather than minting a new dimension value. Nothing caller-controlled
 * reaches the log line through here.
 */
export function readEngineFamily(userAgent: string): string {
    const ua = userAgent ?? "";
    const platform = /iPhone|iPad|iPod/.test(ua)
        ? "ios"
        : /Android/.test(ua)
            ? "android"
            : "desktop";
    // Order matters: iOS Chrome ("CriOS") and iOS Firefox ("FxiOS") both also carry "Safari", and
    // Edge carries "Chrome".
    const browser = /FxiOS|Firefox/.test(ua)
        ? "firefox"
        : /EdgiOS|Edg\//.test(ua)
            ? "edge"
            : /CriOS|Chrome|Chromium/.test(ua)
                ? "chrome"
                : /Safari/.test(ua)
                    ? "safari"
                    : "other";
    return `${platform}-${browser}`;
}

/**
 * The route, collapsed to its template and then hard-restricted to a path-shaped charset.
 *
 * A visitor chooses the path, so this is caller-controlled input heading for a durable log line.
 * `AjaxClientLogController.forLogLine` sanitizes server-side, but a dimension is not a place to rely
 * on someone else's sanitizer: anything outside the allowed set becomes `_`, which also keeps the
 * dimension's cardinality bounded by shape rather than by trust.
 */
export function toRouteDimension(routeTemplate: string | null): string {
    if (!routeTemplate) {
        return "unknown";
    }
    return routeTemplate.slice(0, MAX_ROUTE_LENGTH).replace(/[^A-Za-z0-9/_*.-]/g, "_");
}

// ---------------------------------------------------------------------------------------------
// Runtime
// ---------------------------------------------------------------------------------------------

let installedCleanup: (() => void) | null = null;

/**
 * Session-scoped caps.
 *
 * `sessionStorage` rather than a module variable because a session outlives a page load, and a member
 * on a broken engine visits several pages: without this the "one per session" cap would be one per
 * NAVIGATION, which is the volume the caps exist to prevent. Falls back to the in-memory set when
 * storage throws (Safari private browsing), which degrades the cap to per-page-load rather than
 * dropping it.
 */
const SESSION_CAP_PREFIX = "seavien:unusable-page:";
const inMemoryCaps = new Set<string>();

function claimOncePerSession(key: string): boolean {
    const fullKey = SESSION_CAP_PREFIX + key;
    try {
        if (typeof sessionStorage !== "undefined") {
            if (sessionStorage.getItem(fullKey) === "1") {
                return false;
            }
            sessionStorage.setItem(fullKey, "1");
            return true;
        }
    } catch {
        // fall through to the in-memory cap
    }
    if (inMemoryCaps.has(fullKey)) {
        return false;
    }
    inMemoryCaps.add(fullKey);
    return true;
}

function readScale(win: Window): number {
    return win.visualViewport?.scale ?? 1;
}

function readEngineOffsetTopPx(win: Window): number {
    return Math.max(0, Math.round(win.visualViewport?.offsetTop ?? 0));
}

/**
 * What the LAYOUT sees, not what the runtime meant.
 *
 * Read off the inline style (the runtime writes it with `setProperty` on `documentElement`), never
 * through `getComputedStyle` — a computed read forces style recalculation, and this handler runs on
 * every settled viewport event. An absent variable resolves to `0px` for the layout, because every
 * consumer writes `var(--seavien-app-viewport-offset-top, 0px)`; so absent is reported as 0 rather
 * than skipped, which is the honest reading of what the page is actually doing.
 */
function readPublishedOffsetTopPx(doc: Document): number {
    const raw = doc.documentElement?.style?.getPropertyValue(APP_VIEWPORT_OFFSET_TOP_CSS_VAR) ?? "";
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? Math.round(parsed) : 0;
}

/** The declared contract, or null. One attribute selector, no tree walk, no reflow. */
function readViewportContract(doc: Document): string | null {
    const main = doc.querySelector<HTMLElement>("main[data-viewport-contract]");
    return main?.getAttribute("data-viewport-contract") || null;
}

function reportToBackend(report: UnusablePageReport): void {
    void sendClientLog({
        level: report.kind === "heartbeat" ? "info" : "warn",
        message: report.message,
        extra: {
            // Routes the MDC `clientLogType` the same way `layout-health` does, so these lines are
            // separable in Logs Insights without parsing the message.
            type: "unusable-page",
            ...report.extra,
        },
    });
}

export function installUnusablePageSignals(
    partialOptions: Partial<UnusablePageSignalOptions> = {},
): () => void {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return () => {};
    }
    if (installedCleanup) {
        return installedCleanup;
    }

    const options = { ...DEFAULT_OPTIONS, ...partialOptions };
    const engine = readEngineFamily(typeof navigator === "undefined" ? "" : navigator.userAgent);

    // `checks` is the heartbeat half of principle 2, and it is per invariant on purpose: an invariant
    // that stops being evaluated (its selector renamed, its listener dropped) shows up as its own
    // count going flat while the other keeps moving, which a single aggregate count would hide.
    const checks: Record<UnusablePageInvariant, number> = { "focus-zoom": 0, "offset-contract": 0 };
    const violations: Record<UnusablePageInvariant, number> = { "focus-zoom": 0, "offset-contract": 0 };
    let evaluations = 0;

    // The scale as it stood when the current editable focus began. Null when nothing editable is
    // focused, or when the focus predates the probe.
    let scaleBeforeFocus: number | null = null;
    /** When the `focusin` that armed `scaleBeforeFocus` happened, so a stale latch cannot be judged. */
    let focusAtMs: number | null = null;
    let settleTimer: number | null = null;
    let heartbeatTimer: number | null = null;

    const emit = (report: UnusablePageReport) => {
        try {
            options.reporter(report);
        } catch {
            // Instrumentation must never surface to the page it is instrumenting.
        }
    };

    const emitViolation = (invariant: UnusablePageInvariant, route: string, detail: Record<string, unknown>) => {
        violations[invariant] += 1;

        // The counter line: once per (invariant, route) per session. This is what the alarm counts, so
        // it is deliberately the cheapest thing that can carry the three dimensions.
        if (claimOncePerSession(`violation:${invariant}:${route}`)) {
            emit({
                kind: "violation",
                invariant,
                route,
                engine,
                message: `${UNUSABLE_PAGE_VIOLATION_MARKER} invariant=${invariant} route=${route} engine=${engine}`
                    + ` violations=${violations[invariant]} checks=${checks[invariant]}`,
                extra: { invariant, route, engine, violations: violations[invariant], checks: checks[invariant] },
            });
        }

        // The sampled detail behind it: 1-in-100, at most one per session. Enough to know WHICH field
        // and WHICH engine — not enough to debug from, which is what the local iOS rig is for.
        //
        // ⚠️ The cap is one per SESSION, not one per invariant, so when both invariants fire in the
        // same pass the first one called takes the slot. `evaluate()` therefore runs the offset
        // contract FIRST — see the note there. Do not reorder it back.
        if (options.random() < options.detailSampleRate && claimOncePerSession("detail")) {
            emit({
                kind: "detail",
                invariant,
                route,
                engine,
                message: `${UNUSABLE_PAGE_DETAIL_MARKER} invariant=${invariant} route=${route} engine=${engine}`,
                extra: { invariant, route, engine, ...detail },
            });
        }
    };

    const evaluate = () => {
        evaluations += 1;
        const route = toRouteDimension(currentRouteTemplate());
        const activeElement = document.activeElement;
        const editableFocused = isEditableElement(activeElement);
        const scale = readScale(window);

        if (!editableFocused) {
            scaleBeforeFocus = null;
            focusAtMs = null;
        }

        // ⚠️ THE OFFSET CONTRACT RUNS FIRST, and the order is load-bearing rather than arbitrary.
        //
        // On the surface where this class was found, BOTH invariants fire in the same pass — the 15px
        // field provokes the zoom, the zoom provokes the misclassification, the misclassification puts
        // the shell off the screen. The sampled detail slot is one per session, so whichever invariant
        // is called first takes it. I2 is the one worth having: it names the CAUSE and it is the one
        // that pages. Focus zoom is the trigger, it is reported product-wide anyway, and its payload is
        // reconstructible from a font size.
        const contract = readViewportContract(document);
        const publishedOffsetTopPx = readPublishedOffsetTopPx(document);
        const engineOffsetTopPx = readEngineOffsetTopPx(window);
        const offsetContract = evaluateOffsetContract({
            hasContract: contract !== null,
            editableFocused,
            scale,
            publishedOffsetTopPx,
            engineOffsetTopPx,
        });
        if (offsetContract !== "not-applicable") {
            checks["offset-contract"] += 1;
        }
        if (offsetContract === "violation") {
            emitViolation("offset-contract", route, {
                contract,
                publishedOffsetTopPx,
                engineOffsetTopPx,
                scale: Math.round(scale * 1000) / 1000,
                editableFocused,
                visualViewportHeight: Math.round(window.visualViewport?.height ?? 0),
                innerHeight: Math.round(window.innerHeight ?? 0),
            });
        }

        const element = activeElement as HTMLElement | null;
        const msSinceFocus = focusAtMs === null ? null : Date.now() - focusAtMs;

        // The computed font size is now part of the PREDICATE, not just the payload, so it has to be
        // read before the verdict rather than inside the violation branch. It forces a style recalc,
        // which this file is careful never to pay on an ordinary viewport event — so read it only once
        // every cheap condition already points at a violation. In every other case the value cannot
        // change the verdict, and `null` is what the invariant is told.
        const pointsAtViolation =
            editableFocused
            && scaleBeforeFocus !== null
            && scaleBeforeFocus <= SCALE_AT_REST_TOLERANCE
            && scale > SCALE_AT_REST_TOLERANCE
            && msSinceFocus !== null
            && msSinceFocus <= FOCUS_ZOOM_JUDGEMENT_WINDOW_MS;
        const fontSizePx = pointsAtViolation ? readComputedFontSize(element) : null;

        const focusZoom = evaluateFocusZoom({
            editableFocused,
            scaleBeforeFocus,
            scaleNow: scale,
            msSinceFocus,
            fontSizePx,
        });
        if (focusZoom !== "not-applicable") {
            checks["focus-zoom"] += 1;
        }
        if (focusZoom === "violation") {
            emitViolation("focus-zoom", route, {
                scale: Math.round(scale * 1000) / 1000,
                scaleBeforeFocus,
                msSinceFocus,
                tagName: element?.tagName?.toLowerCase() ?? "unknown",
                // The one field that names the fix. Computed, because the trigger is frequently
                // INHERITED — Tailwind's preflight sets `font-size: 100%` on form controls, so a field
                // with no text class of its own takes a `text-sm` off an ancestor and no static pass
                // can resolve that (which is exactly `check-mobile-field-font.mjs`'s blind spot).
                // Read once above, where it is part of the verdict; reusing it here rather than
                // recomputing keeps this branch to a single style recalc.
                fontSizePx,
                fieldName: element?.getAttribute("name") ?? element?.getAttribute("data-playwright-id") ?? null,
            });
        }
    };

    const scheduleEvaluation = (delayMs: number) => {
        if (settleTimer !== null) {
            window.clearTimeout(settleTimer);
        }
        settleTimer = window.setTimeout(() => {
            settleTimer = null;
            evaluate();
        }, delayMs);
    };

    const onViewportEvent = () => scheduleEvaluation(options.viewportSettleMs);

    const onFocusIn = (event: Event) => {
        if (!isEditableElement(event.target as Element | null)) {
            return;
        }
        // Read at focusin, which fires BEFORE the engine applies its zoom — that is what makes this
        // "the scale immediately before the focus" rather than "the scale after whatever happened".
        scaleBeforeFocus = readScale(window);
        focusAtMs = Date.now();
        scheduleEvaluation(options.focusSettleMs);
    };

    const onFocusOut = () => {
        scaleBeforeFocus = null;
        focusAtMs = null;
    };

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", onViewportEvent);
    visualViewport?.addEventListener("scroll", onViewportEvent);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);

    // One pass at install, so `evaluations` is never 0 on a page that happened to see no events. A
    // heartbeat that only proves "an event fired" is a weaker claim than "the probe ran".
    evaluate();

    heartbeatTimer = window.setTimeout(() => {
        heartbeatTimer = null;
        if (options.random() >= options.heartbeatSampleRate || !claimOncePerSession("heartbeat")) {
            return;
        }
        const route = toRouteDimension(currentRouteTemplate());
        emit({
            kind: "heartbeat",
            invariant: null,
            route,
            engine,
            message: `${UNUSABLE_PAGE_HEARTBEAT_MARKER} route=${route} engine=${engine}`
                + ` evaluations=${evaluations}`
                + ` focusZoomChecks=${checks["focus-zoom"]} offsetChecks=${checks["offset-contract"]}`
                + ` focusZoomViolations=${violations["focus-zoom"]} offsetViolations=${violations["offset-contract"]}`,
            extra: {
                route,
                engine,
                evaluations,
                checks: { ...checks },
                violations: { ...violations },
            },
        });
    }, options.heartbeatDelayMs);

    installedCleanup = () => {
        visualViewport?.removeEventListener("resize", onViewportEvent);
        visualViewport?.removeEventListener("scroll", onViewportEvent);
        document.removeEventListener("focusin", onFocusIn, true);
        document.removeEventListener("focusout", onFocusOut, true);
        if (settleTimer !== null) {
            window.clearTimeout(settleTimer);
        }
        if (heartbeatTimer !== null) {
            window.clearTimeout(heartbeatTimer);
        }
        settleTimer = null;
        heartbeatTimer = null;
        installedCleanup = null;
    };

    return installedCleanup;
}

/**
 * `getComputedStyle` is a forced style recalculation, so it is confined to the one place that has
 * already earned it: a violation that passed the once-per-session cap.
 */
function readComputedFontSize(element: HTMLElement | null): number | null {
    if (!element || typeof window.getComputedStyle !== "function") {
        return null;
    }
    try {
        const parsed = Number.parseFloat(window.getComputedStyle(element).fontSize);
        return Number.isFinite(parsed) ? Math.round(parsed * 10) / 10 : null;
    } catch {
        return null;
    }
}

export function resetUnusablePageSignalsForTests(): void {
    installedCleanup?.();
    inMemoryCaps.clear();
    try {
        if (typeof sessionStorage !== "undefined") {
            for (const key of Object.keys(sessionStorage)) {
                if (key.startsWith(SESSION_CAP_PREFIX)) {
                    sessionStorage.removeItem(key);
                }
            }
        }
    } catch {
        // nothing to clear
    }
}
